import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRANDS, CATEGORIES, resolveImage } from "@/data/catalog";
import { adminCreateProduct, adminUpdateProduct, adminUpload } from "@/lib/api";

const SPEC_FIELDS = ["type", "tonnage", "btu", "refrigerant", "seer2", "eer2", "hspf2", "voltage", "phase", "discharge", "nox", "zones", "staticPressure", "series", "lineSet", "wifi", "dimensions", "weight", "compatibility"];

const EMPTY = {
  name: "", brandSlug: "midea", categorySlug: "mini-vrf-indoor-units", extraCategories: [], model: "", sku: "",
  priceVisibility: "request", price: "", salePrice: "", status: "",
  featured: false, clearance: false, active: true,
  shortDescription: "", description: "", featuresText: "",
  specs: {}, images: [], seoTitle: "", seoDescription: "",
};

export default function ProductForm({ open, onClose, product, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (product) {
      setF({
        ...EMPTY,
        ...product,
        extraCategories: (product.categories || []).filter((c) => c !== product.categorySlug),
        price: product.price ?? "",
        salePrice: product.salePrice ?? "",
        sku: product.sku ?? "",
        status: product.status ?? "",
        shortDescription: product.shortDescription ?? "",
        description: product.description ?? "",
        featuresText: (product.features || []).join("\n"),
        specs: product.specs || {},
        images: product.images || [],
        seoTitle: product.seoTitle ?? "",
        seoDescription: product.seoDescription ?? "",
      });
    } else {
      setF(EMPTY);
    }
  }, [product, open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSpec = (k, v) => setF((p) => ({ ...p, specs: { ...p.specs, [k]: v } }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUpload(file);
      set("images", [...f.images, url]);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!f.name || !f.model) {
      toast.error("Name and model are required");
      return;
    }
    setSaving(true);
    try {
      const specs = {};
      SPEC_FIELDS.forEach((k) => {
        const v = f.specs[k];
        if (v !== "" && v !== undefined && v !== null) specs[k] = k === "btu" ? Number(v) : v;
      });
      const payload = {
        name: f.name,
        brandSlug: f.brandSlug,
        categorySlug: f.categorySlug,
        categories: Array.from(new Set([f.categorySlug, ...(f.extraCategories || [])])),
        model: f.model,
        sku: f.sku || null,
        priceVisibility: f.priceVisibility,
        price: f.price === "" ? null : Number(f.price),
        salePrice: f.salePrice === "" ? null : Number(f.salePrice),
        status: f.status || null,
        featured: !!f.featured,
        clearance: !!f.clearance,
        active: !!f.active,
        shortDescription: f.shortDescription || null,
        description: f.description || null,
        features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
        specs,
        images: f.images,
        seoTitle: f.seoTitle || null,
        seoDescription: f.seoDescription || null,
      };
      if (product) await adminUpdateProduct(product.id, payload);
      else await adminCreateProduct(payload);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{product ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="pf-name" /></div>
            <div><Label>Model</Label><Input value={f.model} onChange={(e) => set("model", e.target.value)} className="mt-1 font-mono" data-testid="pf-model" /></div>
            <div><Label>SKU</Label><Input value={f.sku} onChange={(e) => set("sku", e.target.value)} className="mt-1" /></div>
            <div>
              <Label>Brand</Label>
              <Select value={f.brandSlug} onValueChange={(v) => set("brandSlug", v)}>
                <SelectTrigger className="mt-1" data-testid="pf-brand"><SelectValue /></SelectTrigger>
                <SelectContent>{BRANDS.map((b) => <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={f.categorySlug} onValueChange={(v) => set("categorySlug", v)}>
                <SelectTrigger className="mt-1" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Also appears in (optional)</Label>
            <p className="mt-1 text-xs text-slate-400">Assign extra categories for cross-listed models (e.g. a multi-zone condenser also in Mini VRF Outdoor). The Category above stays the primary.</p>
            <div className="mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
              {CATEGORIES.filter((c) => c.slug !== f.categorySlug).map((c) => (
                <label key={c.slug} className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox
                    checked={(f.extraCategories || []).includes(c.slug)}
                    onCheckedChange={(v) => set("extraCategories", v ? [...(f.extraCategories || []), c.slug] : (f.extraCategories || []).filter((x) => x !== c.slug))}
                    data-testid={`pf-extra-category-${c.slug}`}
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label>Price Visibility</Label>
              <Select value={f.priceVisibility} onValueChange={(v) => set("priceVisibility", v)}>
                <SelectTrigger className="mt-1" data-testid="pf-visibility"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="request">Request Price</SelectItem><SelectItem value="show">Show Price</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Price ($)</Label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="mt-1" data-testid="pf-price" /></div>
            <div><Label>Sale / Clearance ($)</Label><Input type="number" value={f.salePrice} onChange={(e) => set("salePrice", e.target.value)} className="mt-1" /></div>
          </div>

          <div><Label>Status (optional)</Label><Input value={f.status} onChange={(e) => set("status", e.target.value)} placeholder="e.g. In Stock, Made to Order" className="mt-1" /></div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.featured} onCheckedChange={(v) => set("featured", !!v)} data-testid="pf-featured" /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.clearance} onCheckedChange={(v) => set("clearance", !!v)} data-testid="pf-clearance" /> Clearance</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.active} onCheckedChange={(v) => set("active", !!v)} data-testid="pf-active" /> Active (visible)</label>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Specifications</Label>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              {SPEC_FIELDS.map((k) => (
                <div key={k}><Label className="text-xs capitalize">{k}</Label><Input value={f.specs[k] ?? ""} onChange={(e) => setSpec(k, e.target.value)} className="mt-1" /></div>
              ))}
            </div>
          </div>

          <div><Label>Short description</Label><Input value={f.shortDescription} onChange={(e) => set("shortDescription", e.target.value)} className="mt-1" /></div>
          <div><Label>Full description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1 min-h-[100px]" /></div>
          <div><Label>Key features (one per line)</Label><Textarea value={f.featuresText} onChange={(e) => set("featuresText", e.target.value)} className="mt-1 min-h-[80px]" /></div>

          <div>
            <Label>Images</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {f.images.map((img, i) => (
                <div key={i} className="relative h-20 w-20 overflow-hidden rounded-sm border border-slate-200">
                  <img src={resolveImage(img)} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute right-0 top-0 bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-sm border border-dashed border-slate-300 text-slate-400 hover:border-sky-600">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="pf-upload" />
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="pf-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
